import { ApolloClient } from 'apollo-client'
import { NormalizedCacheObject } from 'apollo-cache-inmemory'
import { useHistory } from 'react-router-dom'
import { History } from 'history'
import { authorizationKeys } from 'constants/localStorageKeys'
import { useApolloClient } from '@apollo/react-hooks'
import { routes } from 'constants/routes'
import { useGetUserCredentialsQuery, useRevokeOAuthMutation } from 'gqlTypes'
import { initializeLocalApolloState } from 'graphql/cache'

type Location = History.LocationDescriptor

interface SignOutProps {
  client: ApolloClient<object>
  history: History
  location?: Location
}

export const signOut = async ({
  client,
  history,
  location = routes.SIGN_IN.create({}),
}: SignOutProps) => {
  /**
   * The order we follow when cleaning things up is important.
   * If we clean Apollo's cache before removing all local storage entries,
   * Apollo's cache subscriptions will be notified and, if they access the local
   * storage (e.g. to verify the list of authorizations), they will wrongly see
   * there are still ongoing sessions.
   */
  authorizationKeys.forEach(key => localStorage.removeItem(key))

  // This makes sure we don't have operations unfinished that might
  // cause error when the user logs out.
  client.stop()

  await client.clearStore()

  // We need to reinitialize the base apolloStore again because the state is now blank
  initializeLocalApolloState(
    (client as ApolloClient<NormalizedCacheObject>).cache
  )

  // This is basically to keep typescript nonsense validations quiet
  if (typeof location === 'string') {
    history.replace(location)
  } else {
    history.replace(location)
  }
}

const useSignOut = (location?: Location) => {
  const client = useApolloClient()
  const history = useHistory()

  const { data } = useGetUserCredentialsQuery({
    fetchPolicy: 'cache-only',
  })

  const [revokeOAuth] = useRevokeOAuthMutation({
    onError: 'do-nothing',
  })

  return () => {
    if (data?.userCredentials) {
      revokeOAuth({
        variables: {
          token: data?.userCredentials?.authToken,
        },
      })
    }
    signOut({ client, history, location })
  }
}

export default useSignOut
